import { styled } from "@linaria/react";
import React from "react";
import type { Project } from "../../../../../../../../api/useProjects";
import type { Source } from "../../../../../../../../api/useSources";
import { useCanUserDoOnProject } from "../../../../../../../canUserDo";
import t from "../../../../../../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import ChartCard from "@ui/Highcharts/ui/Card";
import Switch from "@ui/Switch";
import ActionMenu from "../../../../../../Analysis/AnalysisActionMenu";
import PeriodSelector from "../../../FaultEvent/components/PeriodSelector";
import cx from "classnames";
import ToggleGroupBreaks from "../../../../../../Analysis/ToggleGroupBreaks";
import type { SelectedQuantity } from "../../../../../../useSelectedQuantities";
import GroupUnitInput, { useGroupUnitState } from "../../../../../../Measures/GroupUnitInput";
import { fonts } from "@ui";
import type { Analysis } from "../../../../../../../../api/Analysis";
import InfoTooltip from "../../../../../../Chart/InfoTooltip";
import Selector from "@ui/Selector";
import type { MeasuresChartType } from "../../../../../../Measures/MeasuresChartType";
import type { ActiveAnalysis } from "../../../../../../../../api/useActiveAnalysesStream";
import TogglePublicationDisplay, {
  usePublicationFlagDisplay,
} from "../../../../../../Analysis/TogglePublicationDisplay";
import { ChartMenu } from "../../../../../../Chart/Menu";
import { useBreaksBetweenGroups } from "../../../../../../Analysis/useBreaksBetweenGroups";
import { FullViewportButtonMarker, FullViewportTrigger } from "@ui/FullViewportButton";
import { useSettings } from "../../../../../../../../api/useSettings";
import ToggleDisplayInvalidPoints, {
  useDisplayInvalidPoints,
} from "../../../../../../Analysis/ToggleDisplayInvalidPoints";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import {
  ExportWidgetButton,
  exportWidgetTarget,
  HIDE_ON_WIDGET_EXPORT,
} from "@ui/ExportWidgetButton";
import { useEventPeriod } from "../../../../../hooks/useEventPeriod";
import Button from "@ui/Button";
import { RefreshLine } from "@ui/IconV2/react";
import Text from "@ui/Text";
import { generateExportFileName } from "diagnostics-lib/string";
import type { Event } from "../../../../../../../../api/useEvents";
import EvolutionSubHeader from "../../../../../../EvolutionSubHeader";

import useActiveAnalysesWithDisplayFilters from "../../../../../../../../api/useActiveAnalysesWithDisplayFilters";
import MeasureHighCharts from "./MeasureHighCharts";
import { DisplayFiltersBlankState } from "../../../../../../EvolutionSubHeader/DisplayFiltersBlankState";

const Description = styled.div`
  font: ${fonts.body2};
  margin-left: auto;
`;

interface P {
  project: Project;
  analyses: Analysis[];
  activeAnalyses: ActiveAnalysis[];
  rawActiveAnalysesCount: number;
  source: Source;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  samplingDescription: string | null;
  selectedChartOption: MeasuresChartType;
  setSelectedChartOption: (option: MeasuresChartType) => void;
  allAnalysesMode: boolean;
  setAllAnalysesMode: (v: React.SetStateAction<boolean>) => void;
  event: Event;
}

const MeasuresEvolution = ({
  project,
  source,
  analyses,
  activeAnalyses,
  rawActiveAnalysesCount,
  selectedQuantities,
  samplingDescription,
  selectedChartOption,
  setSelectedChartOption,
  allAnalysesMode,
  setAllAnalysesMode,
  event,
}: P) => {
  const projectId = project.id;

  const { displayFilterRemovesAllActiveAnalyses } = useActiveAnalysesWithDisplayFilters(project.id);

  const displayFilterRemovesAllActiveAnalysesInCurrentMode =
    allAnalysesMode === false && displayFilterRemovesAllActiveAnalyses;

  const settings = useSettings();
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const canUserReadProject = useCanUserDoOnProject("PROJECT_READ", project.id);
  const [breakBetweenGroups] = useBreaksBetweenGroups({
    scope: projectId,
  });
  const [publicationFlagDisplay] = usePublicationFlagDisplay({
    scope: projectId,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: projectId,
  });
  const [axisGroupedByUnit] = useGroupUnitState({
    scope: projectId,
  });

  const canChangeSampleMode = Boolean(activeAnalyses.length);

  //action allowed if udpdate, delete permission
  const [_, __, ___, currentPeriodIsDefaultPeriod, setPeriodToDefault] = useEventPeriod(
    allAnalysesMode
      ? {
          event,
          source,
          project,
          boundaries: "analyses",
        }
      : {
          event,
          source,
          project,
          analyses: activeAnalyses,
          boundaries: "active-analyses",
        },
  );

  const displayGroupUnitInput = React.useMemo(
    () => selectedQuantities.length > 1,
    [selectedQuantities],
  );

  const someQuantityStdAvailable = React.useMemo(
    () => selectedQuantities.some(value => value.quantity.formula === null),
    [selectedQuantities],
  );

  const chartTypeOptions = React.useMemo(
    () => [
      {
        value: "value" as const,
        label: t("measures.chart.toggle.byValue"),
        description: t("measures.chart.toggle.byValue.description"),
      },
      {
        value: "std" as const,
        label: t("measures.chart.toggle.byStd"),
        description: t("measures.chart.toggle.byStd.description"),
        disabled: !someQuantityStdAvailable,
      },
    ],
    [someQuantityStdAvailable],
  );

  React.useEffect(() => {
    if (!selectedQuantities.some(value => value.quantity.formula === null))
      setSelectedChartOption("value");
  }, [selectedQuantities, setSelectedChartOption]);

  const handleSwitchallAnalysesMode = React.useCallback(
    () => setAllAnalysesMode(allAnalysesMode => !allAnalysesMode),
    [setAllAnalysesMode],
  );

  const title = React.useMemo(
    () => (
      <>
        <Text as="span" type="h4">
          {t("measures.chart.title")}
        </Text>
        {rawActiveAnalysesCount > 0 ? (
          <Switch
            checked={allAnalysesMode}
            disabled={rawActiveAnalysesCount === 0}
            onChange={handleSwitchallAnalysesMode}
            className={HIDE_ON_WIDGET_EXPORT}
          >
            {t("measures.chart.title.switch")}
          </Switch>
        ) : null}
        <div style={{ marginLeft: "auto" }}>
          <ExportWidgetButton
            fileName={generateExportFileName({
              filename: t("measures.chart.title"),
              projectName: project.name,
              extension: "png",
            })}
          />
          <FullViewportTrigger style={{ marginLeft: spacings.xs }} />
        </div>
      </>
    ),
    [allAnalysesMode, handleSwitchallAnalysesMode, project.name, rawActiveAnalysesCount],
  );

  const subtitle = React.useMemo(
    () => (
      <EvolutionSubHeader
        projectIdToFilter={projectId}
        allAnalysesMode={allAnalysesMode}
        chartOptions={
          <InfoTooltip
            title={t("symptom.chart.header.option.title")}
            options={chartTypeOptions}
            link="/symptoms-page"
          >
            <div className={HIDE_ON_WIDGET_EXPORT}>
              <Selector
                options={chartTypeOptions}
                value={selectedChartOption}
                onChange={setSelectedChartOption}
              />
            </div>
          </InfoTooltip>
        }
        periodSettings={
          <>
            {!currentPeriodIsDefaultPeriod && (
              <Button variant="tertiary" onClick={setPeriodToDefault} leftIcon={<RefreshLine />}>
                {t("project.fault_event.fault.chart.period_picker.reset")}
              </Button>
            )}
            {allAnalysesMode === true ? (
              <PeriodSelector
                event={event}
                source={source}
                project={project}
                boundaries="analyses"
              />
            ) : (
              <PeriodSelector
                event={event}
                source={source}
                project={project}
                analyses={activeAnalyses}
                boundaries="active-analyses"
              />
            )}
          </>
        }
        actionMenu={
          displayFilterRemovesAllActiveAnalysesInCurrentMode ? null : (
            <>
              <ActionMenu
                event={event}
                analyses={analyses}
                project={project}
                canWriteProject={canUserWriteProject}
                canUserReadProject={canUserReadProject}
                allAnalysesMode={allAnalysesMode}
                className={HIDE_ON_WIDGET_EXPORT}
              />
              {samplingDescription ? (
                <Description className={HIDE_ON_WIDGET_EXPORT}>{samplingDescription}</Description>
              ) : null}
            </>
          )
        }
        visibilitySettings={
          <ChartMenu
            marker={
              publicationFlagDisplay ||
              !breakBetweenGroups ||
              displayInvalidPoints ||
              (axisGroupedByUnit && displayGroupUnitInput)
            }
            tooltipOverlay={t("chart.display_options.tooltip")}
          >
            <ToggleDisplayInvalidPoints projectId={projectId} />
            {displayGroupUnitInput ? <GroupUnitInput projectId={projectId} /> : null}
            {canChangeSampleMode ? (
              <TogglePublicationDisplay allAnalysesMode={allAnalysesMode} projectId={project.id} />
            ) : null}
            <ToggleGroupBreaks projectId={projectId} groupables={analyses} />
          </ChartMenu>
        }
      />
    ),
    [
      activeAnalyses,
      allAnalysesMode,
      analyses,
      axisGroupedByUnit,
      breakBetweenGroups,
      canChangeSampleMode,
      canUserReadProject,
      canUserWriteProject,
      chartTypeOptions,
      currentPeriodIsDefaultPeriod,
      displayFilterRemovesAllActiveAnalysesInCurrentMode,
      displayGroupUnitInput,
      displayInvalidPoints,
      event,
      project,
      projectId,
      publicationFlagDisplay,
      samplingDescription,
      selectedChartOption,
      setPeriodToDefault,
      setSelectedChartOption,
      source,
    ],
  );

  if (displayFilterRemovesAllActiveAnalysesInCurrentMode)
    return (
      <ChartCard
        title={title}
        subtitle={subtitle}
        className={cx(measureEvolutionChart, FullViewportButtonMarker, exportWidgetTarget)}
      >
        <DisplayFiltersBlankState projectId={projectId} />
      </ChartCard>
    );

  return (
    <MeasureHighCharts
      project={project}
      title={title}
      subtitle={subtitle}
      activeAnalyses={activeAnalyses}
      selectedQuantities={selectedQuantities}
      allAnalysesMode={allAnalysesMode}
      analyses={analyses}
      event={event}
      selectedChartOption={selectedChartOption}
      source={source}
    />
  );
};

export default React.memo(MeasuresEvolution);

//#region CSS
const measureEvolutionChart = css`
  row-gap: ${spacings.m};
`;

//#endregion CSS
